var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCurrentAccountId } from '@zapier/identity';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { emitUserInteractionEvent } from '../utils/avroEvents';
import { isEmptyObject } from '../utils/isEmptyObject';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useMediaProps } from '../../utils/responsive-utils';
import { useQueryTopNav } from './useQueryTopNav';
import { FALLBACK_TOP_NAV_RESPONSE } from '../constants';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, UNIVERSAL_SIDEBAR_WIDTH, BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN, } from '../constants';
import { useQuerySession } from './useQuerySession';
const defaultContext = {
    apiHostname: '',
    bffHostname: '',
    canForceSidebarOpen: false,
    canTopbarFitBackToAppButton: false,
    canTopbarFitLinks: false,
    currentAccountId: undefined,
    currentUrl: '',
    isCreateMenuOpen: false,
    isLoadingLoggedIn: true,
    isLoadingTopNav: true,
    isLoggedIn: false,
    isMoreLinksMenuOpen: false,
    isOnAppPage: false,
    isInAppExperience: false,
    isSearchFullWidth: true,
    isSidebarControlledByTopbar: false,
    isSidebarForcedOpen: false,
    isSidebarOpenedByTopbar: false,
    isSidebarRendered: false,
    onToggleSidebarVisibility: () => { },
    setIsCreateMenuOpen: () => { },
    setIsMoreLinksMenuOpen: () => { },
    setIsSidebarForcedOpen: () => { },
    setIsSidebarOpenedByTopbar: () => { },
    setIsSidebarRendered: () => { },
    shouldRenderCallToAction: false,
    shouldRenderUpgradeCTA: false,
    shouldRenderSearch: true,
    shouldRenderSidebar: false,
    shouldRenderTopbarLinks: false,
    shouldRenderTOS: false,
    shouldRenderUniversalNav: true,
    sidebarWidth: 0,
    topbarSize: 'medium',
    topNavData: {},
    getFullyQualifiedURL: (path) => path || undefined,
};
export const UniversalNavContext = createContext(defaultContext);
export const useUniversalNavContext = () => useContext(UniversalNavContext);
const isCurrentUrlAnAuthPage = (currentUrl) => {
    return (currentUrl.includes('/login') ||
        currentUrl.includes('/sign-up') ||
        currentUrl.includes('/oauth/authorize'));
};
const isCurrentUrlAnAppPage = (currentUrl, mode) => {
    if (mode) {
        return mode === 'in-app' ? true : false;
    }
    return currentUrl.includes('/app/');
};
const isCurrentUrlAnOnboardingPage = (currentUrl) => {
    return currentUrl.includes('/app/onboarding');
};
const shouldRenderSidebarForRoute = ({ currentUrl, mode, }) => {
    return (isCurrentUrlAnAppPage(currentUrl, mode) &&
        !isCurrentUrlAnAuthPage(currentUrl) &&
        !isCurrentUrlAnOnboardingPage(currentUrl));
};
export const shouldRenderTopbarLinksForRoute = ({ currentUrl, mode, }) => {
    return (!isCurrentUrlAnAppPage(currentUrl, mode) &&
        !isCurrentUrlAnAuthPage(currentUrl));
};
const getShouldRenderSidebar = ({ canTopbarFitLinks, currentUrl, isCreateMenuOpen, isMoreLinksMenuOpen, mode, }) => {
    if (isCreateMenuOpen)
        return true;
    if (isMoreLinksMenuOpen)
        return true;
    if (mode && mode === 'navigation') {
        return canTopbarFitLinks === false ? true : false;
    }
    if (mode && mode === 'in-app') {
        return true;
    }
    if (shouldRenderSidebarForRoute({ currentUrl, mode })) {
        return true;
    }
    return false;
};
const getShouldRenderSearch = ({ currentUrl }) => {
    if (isCurrentUrlAnAuthPage(currentUrl) ||
        isCurrentUrlAnOnboardingPage(currentUrl)) {
        return false;
    }
    return true;
};
const getSidebarWidth = ({ canForceSidebarOpen, isSidebarForcedOpen, }) => {
    if (canForceSidebarOpen && isSidebarForcedOpen) {
        return UNIVERSAL_SIDEBAR_WIDTH;
    }
    return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};
export const UniversalNavProvider = (_a) => {
    var { accountData: accountUsageData, accountsListData, apiHostname, bffHostname, currentUrl, shouldSidebarBeInitiallyCollapsed = false, mode, sessionData: sessionDataFromProps, shouldRenderTOS, urlPathPrefix } = _a, props = __rest(_a, ["accountData", "accountsListData", "apiHostname", "bffHostname", "currentUrl", "shouldSidebarBeInitiallyCollapsed", "mode", "sessionData", "shouldRenderTOS", "urlPathPrefix"]);
    const { data: sessionData } = useQuerySession(sessionDataFromProps, apiHostname);
    const currentAccountId = useCurrentAccountId() || (sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentAccountId);
    const isLoggedIn = sessionData ? sessionData.isLoggedIn : false;
    const isInAppExperience = isCurrentUrlAnAppPage(currentUrl, mode) &&
        false === isCurrentUrlAnOnboardingPage(currentUrl) &&
        false === isCurrentUrlAnAuthPage(currentUrl);
    const { isLoading: isLoadingTopNav, data: topNav = FALLBACK_TOP_NAV_RESPONSE, } = useQueryTopNav({
        apiHostname: apiHostname || '',
        bffHostname: bffHostname || '',
        currentAccountId,
        customUserId: sessionData === null || sessionData === void 0 ? void 0 : sessionData.customUserId,
        isInAppExperience,
    });
    const isOnAppPage = isCurrentUrlAnAppPage(currentUrl, mode);
    const { isSearchFullWidth, isSidebarControlledByTopbar, canForceSidebarOpen, canTopbarFitBackToAppButton, canTopbarFitLinks, topbarSize, } = useMediaProps({
        '(min-width: 0px)': {
            isSidebarControlledByTopbar: true,
            isSearchFullWidth: true,
            canForceSidebarOpen: false,
            canTopbarFitBackToAppButton: false,
            canTopbarFitLinks: false,
            topbarSize: 'small',
        },
        '(min-width: 450px)': {
            canTopbarFitBackToAppButton: isEmptyObject(topNav.callToAction) && isEmptyObject(topNav.upgradeCTA),
            topbarSize: 'small',
        },
        '(min-width: 800px)': Object.assign({}, (isEmptyObject(topNav.upgradeCTA)
            ? {
                canTopbarFitBackToAppButton: true,
                topbarSize: 'medium',
            }
            : { canTopbarFitBackToAppButton: false, topbarSize: 'small' })),
        '(min-width: 1040px)': {
            topbarSize: 'medium',
            canTopbarFitBackToAppButton: true,
            isSidebarControlledByTopbar: false,
            isSearchFullWidth: false,
            canTopbarFitLinks: true,
        },
        [`(min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN}px)`]: {
            canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl, mode }),
        },
    });
    const shouldRenderTopbarLinks = canTopbarFitLinks &&
        shouldRenderTopbarLinksForRoute({
            currentUrl,
            mode,
        });
    const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
    const [isMoreLinksMenuOpen, setIsMoreLinksMenuOpen] = useState(false);
    const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
    const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage('UniversalSidebar:isSidebarForcedOpen', !shouldSidebarBeInitiallyCollapsed);
    const [isSidebarRendered, setIsSidebarRendered] = useState(true);
    const shouldRenderSidebar = getShouldRenderSidebar({
        canTopbarFitLinks,
        currentUrl,
        isCreateMenuOpen,
        isMoreLinksMenuOpen,
        mode,
    });
    const shouldRenderSearch = getShouldRenderSearch({
        currentUrl,
    });
    const onToggleSidebarVisibility = () => {
        emitUserInteractionEvent({
            event_action: 'click',
            option_selected: `UniversalTopbar SidebarVisibilityToggle`,
        });
        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
    };
    const sidebarWidth = getSidebarWidth({
        canForceSidebarOpen,
        isSidebarForcedOpen,
        isSidebarRendered,
        isSidebarControlledByTopbar,
    });
    const memoizedFullyQualifiedUrl = useCallback((link) => {
        if (link && urlPathPrefix && link.startsWith('/'))
            return urlPathPrefix + link;
        return link || undefined;
    }, [urlPathPrefix]);
    const value = {
        accountUsageData,
        accountsListData,
        currentAccountId,
        isLoadingTopNav,
        canTopbarFitBackToAppButton,
        canTopbarFitLinks,
        canForceSidebarOpen,
        currentUrl,
        isCreateMenuOpen,
        isMoreLinksMenuOpen,
        isLoadingLoggedIn: !sessionData,
        isLoggedIn,
        isOnAppPage,
        isInAppExperience,
        isSearchFullWidth,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
        isSidebarForcedOpen,
        isSidebarRendered,
        mode,
        onToggleSidebarVisibility,
        sessionData: sessionDataFromProps,
        setIsCreateMenuOpen,
        setIsMoreLinksMenuOpen,
        setIsSidebarOpenedByTopbar,
        setIsSidebarForcedOpen,
        setIsSidebarRendered,
        shouldRenderCallToAction: !isCurrentUrlAnOnboardingPage(currentUrl),
        shouldRenderUpgradeCTA: !isCurrentUrlAnOnboardingPage(currentUrl),
        shouldRenderSearch,
        shouldRenderSidebar,
        shouldRenderTopbarLinks,
        shouldRenderTOS,
        shouldRenderUniversalNav: true,
        sidebarWidth,
        topbarSize,
        topNavData: topNav,
        urlPathPrefix,
        getFullyQualifiedURL: memoizedFullyQualifiedUrl,
        apiHostname: apiHostname || '',
        bffHostname: bffHostname || '',
    };
    return (React.createElement(UniversalNavContext.Provider, { value: value }, props.children));
};
//# sourceMappingURL=useUniversalNavContext.js.map
// @zapier/universal-layout@11.2.3 (b1a6e8603)
